import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  OverlayView,
  OverlayViewF,
  PolygonF,
  PolylineF,
} from "@react-google-maps/api";
import IconButton from "./IconButton";
import {
  faBan,
  faCheckCircle,
  faEye,
  faEyeSlash,
  faRotateLeft,
  faTimesCircle,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import useLocation from "../Hooks/useLocation";
import LoadingSpinner from "./Loading/LoadingSpinner";
import MapControl from "./Map/MapControl";
import { SalvoContext } from "contexts/SalvoContext";
import PlaceAutocomplete from "./PlaceAutocomplete";
import css from "./css/Map.module.css";
import { isCurrentTimeBetween } from "../../src/utils/DateTimeUtills";
import { Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ConditionalRender, { Fallback } from "./ConditionalRender";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getDistinctPoints } from "../utils/geofenceUtils";
import useFitBounds from "../Hooks/useFitBounds";
import GeofencesDisplay from "../pages/GeofencePage/Components/GeofencesDisplay";

const defaultContainerStyle = {
  width: "100%",
  height: "600px",
};

const MapContainer = ({
  onChange,
  geofences,
  containerStyle,
  geofenceToEdit,
  onCancelEdit,
}) => {
  const [polygonPaths, setPolygonPaths] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [incomingGeofences, setIncomingGeofences] = useState(geofences);

  const { isLoaded } = useContext(SalvoContext);
  const { userLocation, isLoading } = useLocation({
    lat: 51.441277,
    lng: 5.476341,
  });
  const { t } = useTranslation();
  const markerRef = useRef(null);
  const [map, setMap] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(14);
  const [drawingStarted, setDrawingStarted] = useState(false);
  const [tempPath, setTempPath] = useState([]);
  const [drawingFinished, setDrawingFinished] = useState(false);
  const currentIndex = useRef(0);
  const [editDropdownPosition, setEditDropdownPosition] = useState(null);
  const [polygonEditDropdownPosition, setPolygonEditDropdownPosition] =
    useState(null);
  const vertices = useRef([]);
  const vertexClicked = useRef(null);
  const polygonRef = useRef(null);
  const polylineRef = useRef(null);

  useEffect(() => {
    if (drawingFinished) {
      onChange(mapPathsToPoints(polygonPaths));
    }
  }, [drawingFinished]);

  useEffect(() => {
    if (geofenceToEdit) {
      resetStateMapState();
      setDrawingStarted(true);
      const points = getDistinctPoints(geofenceToEdit.points);
      points.map((point) => {
        addPath({ lat: point.latitude, lng: point.longitude });
      });
      finishDrawing();
    }
  }, [geofenceToEdit]);

  document.onfullscreenchange = function (event) {
    let target = event.target;
    let pacContainerElements = document.getElementsByClassName("pac-container");
    if (pacContainerElements.length > 0) {
      let pacContainer = document.getElementsByClassName("pac-container")[0];
      if (pacContainer.parentElement === target) {
        document.getElementsByTagName("body")[0].appendChild(pacContainer);
      } else {
        target.appendChild(pacContainer);
      }
    }
  };

  const handlePlaceSelected = (place) => {
    if (!place || !place.geometry || !place.geometry.location) return null;
    const latlng = place.geometry.location;
    const lat = latlng.lat();
    const lng = latlng.lng();
    const coords = { lat, lng };
    if (markerRef.current !== null) {
      if (map === null) return;
      markerRef.current.setMap(null);
      markerRef.current.setAnimation(window.google.maps.Animation.DROP);
      markerRef.current.setPosition(latlng);
      markerRef.current.setMap(map);
    } else {
      // create the marker
      markerRef.current = createMarker(coords);
    }
    resetStateMapState();
    setCenter(coords);
  };

  const canLoadMap = useMemo(() => {
    return isLoaded && !isLoading;
  }, [isLoaded, isLoading]);

  const [center, setCenter] = useState();

  useEffect(() => {
    setCenter(userLocation);
  }, [userLocation]);

  useFitBounds(map, geofenceToEdit);

  const removePolyVertices = () => {
    for (let i = 0; i < vertices.current.length; i++) {
      vertices.current[i].setMap(null);
    }
    vertices.current = [];
    currentIndex.current = 0;
  };

  const removeLastPolylineVertex = () => {
    vertices.current[currentIndex.current - 1].setMap(null);
    currentIndex.current = currentIndex.current - 1;
  };

  const finishDrawing = () => {
    // remove all the markers from the list
    removePolyVertices();
    setDrawingFinished(true);
    setDrawingStarted(false);

    // We can't call onChange here because the function is cached? and the state of polygonPaths is not updated.
  };

  const createMarkerVertex = (pos) => {
    const m = new window.google.maps.Marker({
      position: pos,
      map: map,
      clickable: true,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE, // Use a circle as the marker
        scale: 4, // Adjust the size of the circle
        fillColor: "#FFFFFF", // White fill color
        fillOpacity: 1, // Fully opaque
        strokeWeight: 1, // Small border
        strokeColor: "#FF0000", // Red border
        zIndex: 1,
      },
    });
    m.index = currentIndex.current;
    m.addListener("click", () => {
      // get the index of the marker
      // if the index is equal to 0 then we are back at the beginning
      if (m.index === 0) finishDrawing();
    });
    currentIndex.current = currentIndex.current + 1;
    vertices.current = [...vertices.current, m];
  };

  const handleMapClick = async (event) => {
    setEditDropdownPosition(null);
    setPolygonEditDropdownPosition(null);

    if (drawingFinished) return;

    if (!drawingStarted) {
      setDrawingStarted(true);
    }

    addPath(event.latLng.toJSON());
  };

  const addPath = (pos) => {
    createMarkerVertex(pos);
    setPolygonPaths((oldPaths) => [...oldPaths, pos]);
    setTempPath([]);
  };

  function mapPathsToPoints(paths) {
    const result = [];
    paths.map((path) => {
      result.push({
        latitude: path.lat,
        longitude: path.lng,
      });
    });
    return result;
  }

  const pathIsPolyline = (newPath) => newPath.length > 1;

  function removeLastPointFromPolygon(vertex) {
    const newPath = removeVertex(vertex);
    if (polylineRef.current !== null) return;
    if (pathIsPolygon(newPath)) {
      setPolygonPaths([...newPath]);
      onChange(mapPathsToPoints(newPath));
    } else {
      resetStateMapState();
    }
  }

  function removeLastPointFromPolyline() {
    const newPath = polygonPaths.slice(0, -1);
    const lastIndex = newPath.length - 1;
    if (pathIsPolyline(newPath)) {
      removeLastPolylineVertex();
      setPolygonPaths(newPath);
    } else {
      resetStateMapState();
    }
  }

  const workOrderIsActive = (wo) => {
    return (
      wo &&
      wo.intervals.some((interval) =>
        isCurrentTimeBetween(interval.start, interval.end)
      )
    );
  };

  useEffect(() => {
    if (filterType === "all") {
      setIncomingGeofences(geofences);
    } else if (filterType === "active") {
      setIncomingGeofences(
        geofences.filter((geofence) =>
          geofence.workOrders.some((workOrder) => workOrderIsActive(workOrder))
        )
      );
    } else if (filterType === "inactive") {
      setIncomingGeofences(
        geofences.filter((geofence) =>
          geofence.workOrders.every(
            (workOrder) => !workOrderIsActive(workOrder)
          )
        )
      );
    } else if (filterType === "none") {
      setIncomingGeofences([]); // Show no geofences
    }
  }, [filterType, geofences]);

  function removeLastPoint() {
    if (polygonRef.current !== null) {
      const lastVertex = polygonPaths.length - 1;
      if (lastVertex < 1) return;
      return removeLastPointFromPolygon(lastVertex);
    }
    return removeLastPointFromPolyline();
  }

  const resetStateMapState = () => {
    removePolyVertices();
    clearPoints();
    setDrawingStarted(false);
    setPolygonEditDropdownPosition(null);
    setDrawingFinished(false);
    polygonRef.current = null;
    polylineRef.current = null;
  };

  function clearPoints() {
    setPolygonPaths([]);
    setTempPath([]);
    onChange([]);
  }

  // small hack
  // when moving to fullscreen the dropdown from the autocomplete is not showing up.
  // This piece of code fixes this problem.
  document.onfullscreenchange = function (event) {
    let target = event.target;
    let pacContainerElements = document.getElementsByClassName("pac-container");
    if (pacContainerElements.length > 0) {
      let pacContainer = document.getElementsByClassName("pac-container")[0];
      if (pacContainer.parentElement === target) {
        document.getElementsByTagName("body")[0].appendChild(pacContainer);
        pacContainer.className += pacContainer.className.replace(
          "fullscreen-pac-container",
          ""
        );
      } else {
        target.appendChild(pacContainer);
        pacContainer.className += " fullscreen-pac-container";
      }
    }
  };

  const createMarker = (pos) => {
    return new window.google.maps.Marker({
      map,
      draggable: false,
      animation: window.google.maps.Animation.DROP,
      position: pos,
    });
  };

  // Handle dragging the polygon to update its path
  const getPathFromPolygon = (polygon) => {
    const path = polygon.getPath();
    return Array.from({ length: path.getLength() }, (_, i) => {
      const latLng = path.getAt(i);
      return { lat: latLng.lat(), lng: latLng.lng() };
    });
  };

  const onEdit = (polygon) => {
    setEditDropdownPosition(null);
    const newPath = getPathFromPolygon(polygon);
    setPolygonPaths(newPath);
    onChange(mapPathsToPoints(newPath));
  };

  const attachPolygonListeners = (polygon) => {
    const path = polygon.getPath();

    // When a vertex is moved
    window.google.maps.event.addListener(path, "set_at", () => {
      onEdit(polygon);
    });

    // When a new vertex is added (e.g., splitting a segment)
    window.google.maps.event.addListener(path, "insert_at", () => {
      onEdit(polygon);
    });

    // When a vertex is removed
    window.google.maps.event.addListener(path, "remove_at", () => {
      onEdit(polygon);
    });
  };

  const handleMouseMove = (e) => {
    if (!drawingStarted || drawingFinished || !e || !e.latLng) return;
    const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    const temp = [polygonPaths[polygonPaths.length - 1], pos];
    setTempPath(temp);
  };

  const cleanupEditButtonClick = () => {
    vertexClicked.current = null;
    setEditDropdownPosition(null);
  };

  const handlePolyRightClick = (e) => {
    // set the vertex that was clicked
    if (e.vertex != null) {
      vertexClicked.current = e.vertex;
      setPolygonEditDropdownPosition(null);
      setEditDropdownPosition(e.latLng.toJSON());
    } else {
      vertexClicked.current = null;
      setEditDropdownPosition(null);
      setPolygonEditDropdownPosition(e.latLng.toJSON());
    }
  };

  // helper to remove a vertex from the polygon
  // returns the new path array
  const removeVertex = (vertex) => {
    polygonRef.current.getPath().removeAt(vertex);
    const newPath = getPathFromPolygon(polygonRef.current);
    return newPath;
  };

  // helper function returning true
  // if the array represents a polygon
  const pathIsPolygon = (path) => path.length > 2;
  const handleEditRemoveClick = (e) => {
    e.stopPropagation();
    setPolygonEditDropdownPosition(null);
    // grab the polygon event
    let newPath = removeVertex(vertexClicked.current);
    if (pathIsPolygon(newPath)) {
      setPolygonPaths([...newPath]);
      onChange(mapPathsToPoints(newPath));
    } else {
      onChange([]);
      resetStateMapState();
    }
    cleanupEditButtonClick();
  };

  const handleEditRemovePolygonClick = (e) => {
    e.stopPropagation();
    resetStateMapState();
  };
  const handleWorkOrderMouseOver = (geofence) => {
    // console.log("geofenceeee ", geofence)
  };

  const getIcon = (type) => {
    switch (type) {
      case "all":
        return (
          <FontAwesomeIcon
            color="black"
            style={{ marginLeft: "-2px" }}
            icon={faEye}
          />
        );
      case "active":
        return (
          <FontAwesomeIcon
            color="black"
            style={{ marginLeft: "-2px" }}
            icon={faCheckCircle}
          />
        );
      case "inactive":
        return (
          <FontAwesomeIcon
            color="black"
            style={{ marginLeft: "-2px" }}
            icon={faTimesCircle}
          />
        );
      case "none":
        return (
          <FontAwesomeIcon
            color="black"
            style={{ marginLeft: "-2px" }}
            icon={faEyeSlash}
          />
        );
      default:
        return (
          <FontAwesomeIcon
            color="black"
            style={{ marginLeft: "-2px" }}
            icon={faEye}
          />
        );
    }
  };

  return (
    <>
      {!canLoadMap ? (
        <div
          style={containerStyle}
          className="d-flex justify-content-center align-items-center"
        >
          <LoadingSpinner />
        </div>
      ) : (
        <GoogleMap
          onLoad={(map) => setMap(map)}
          mapContainerStyle={containerStyle}
          center={center}
          zoom={13}
          onZoomChanged={() => map && setCurrentZoom(map.getZoom())}
          onClick={handleMapClick}
          onMouseMove={handleMouseMove}
          options={{
            disableDoubleClickZoom: true,
            disableDefaultUI: true,
            zoomControl: true,
            clickableIcons: false,
            draggableCursor: !drawingFinished ? "crosshair" : "",
            fullscreenControl: true,
            mapTypeControl: true,
            mapTypeControlOptions: {
              style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
              position: window.google.maps.ControlPosition.BOTTOM_CENTER,
              mapTypeIds: [
                window.google.maps.MapTypeId.ROADMAP,
                window.google.maps.MapTypeId.SATELLITE,
                window.google.maps.MapTypeId.HYBRID,
              ],
            },
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [
                  {
                    visibility: "off",
                  },
                ],
              },
              {
                featureType: "transit.station",
                stylers: [
                  {
                    visibility: "off",
                  },
                ],
              },
            ],
          }}
        >
          {editDropdownPosition && (
            <OverlayViewF
              position={editDropdownPosition}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <ul
                className="list-group"
                role="menu"
                style={{ cursor: "default" }}
              >
                <li
                  onClick={handleEditRemoveClick}
                  className="list-group-item list-group-item-action"
                >
                  Delete point
                </li>
              </ul>
            </OverlayViewF>
          )}
          {polygonEditDropdownPosition && (
            <OverlayViewF
              position={polygonEditDropdownPosition}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <ul
                className="list-group"
                role="menu"
                style={{ cursor: "default" }}
              >
                <li
                  onClick={handleEditRemovePolygonClick}
                  className="list-group-item list-group-item-action"
                >
                  Delete polygon
                </li>
              </ul>
            </OverlayViewF>
          )}
          <ConditionalRender condition={drawingFinished}>
            <PolygonF
              path={polygonPaths}
              onLoad={(r) => {
                polygonRef.current = r;
                attachPolygonListeners(r);
              }}
              onUnmount={() => (polygonRef.current = null)}
              editable={true}
              draggable={true}
              onRightClick={handlePolyRightClick}
              onEdit={onEdit}
              options={{
                fillColor: "#E4032C",
                fillOpacity: 0.2,
                strokeColor: "#E4032C",
                strokeWeight: 2.0,
              }}
            />

            <Fallback>
              <PolylineF
                onLoad={(r) => (polylineRef.current = r)}
                onUnmount={() => (polylineRef.current = null)}
                path={polygonPaths}
                options={{
                  cursor: "crosshair",
                  fillColor: "#E4032C",
                  fillOpacity: "1.0",
                  // strokeColor: '#5c0029',
                  strokeColor: "#E4032C",
                  strokeWeight: 2.0,
                }}
              />

              <PolylineF
                path={tempPath}
                options={{
                  cursor: "crosshair",
                  fillColor: "#F24C59",
                  fillOpacity: "1.0",
                  // strokeColor: '#5c0029',
                  strokeColor: "#F7B5CA",
                  strokeWeight: 2.0,
                  clickable: false,
                }}
              />
            </Fallback>
          </ConditionalRender>
          <MapControl position={window.google.maps.ControlPosition.LEFT_TOP}>
            <PlaceAutocomplete
              types={[]}
              fields={["geometry.location"]}
              className={css.searchBar}
              onPlaceSelected={handlePlaceSelected}
              restrictions={{
                country: ["NL", "DE", "BE", "LU"],
              }}
            />
          </MapControl>

          <MapControl position={window.google.maps.ControlPosition.LEFT_CENTER}>
            <Col
              style={{
                marginLeft: "18px",
                marginTop: "1.25rem",
                gap: "1rem",
                fontSize: "13px",
              }}
            >
              <div className="btn-group dropend">
                <button
                  type="button"
                  className="btn btn-secondary custom-dropdown-button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "3px",
                    color: "white",
                    backgroundColor: "white",
                  }}
                >
                  {getIcon(filterType)}
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <a
                      className="dropdown-item"
                      onClick={() => setFilterType("all")}
                    >
                      <i className="fas fa-eye me-2"></i>
                      {t(
                        "pages.workLocations.registerGeofenceDialog.dropdown_all"
                      )}
                    </a>
                  </li>
                  <li>
                    <a
                      className="dropdown-item"
                      onClick={() => setFilterType("active")}
                    >
                      <i className="fas fa-check-circle me-2"></i>
                      {t(
                        "pages.workLocations.registerGeofenceDialog.dropdown_active"
                      )}
                    </a>
                  </li>
                  <li>
                    <a
                      className="dropdown-item"
                      onClick={() => setFilterType("inactive")}
                    >
                      <i className="fas fa-times-circle me-2"></i>
                      {t(
                        "pages.workLocations.registerGeofenceDialog.dropdown_inactive"
                      )}
                    </a>
                  </li>
                  <li>
                    <a
                      className="dropdown-item"
                      onClick={() => setFilterType("none")}
                    >
                      <i className="fas fa-eye-slash me-2"></i>
                      {t(
                        "pages.workLocations.registerGeofenceDialog.dropdown_none"
                      )}
                    </a>
                  </li>
                </ul>
              </div>

              {/* </div> */}
              <IconButton
                icon={faRotateLeft}
                className={`col bg-light text-black fw-bold mt-2 ${css.mapControlButton}`}
                onClick={removeLastPoint}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "3px",
                }}
              />

              <IconButton
                className={`col bg-light fw-bold mt-2 ${css.mapControlButton}`}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "3px",
                  border: "1px solid red",
                  color: "red",
                }}
                color={"red"}
                icon={faTrashCan}
                onClick={resetStateMapState}
              />

              <ConditionalRender condition={geofenceToEdit}>
                <IconButton
                  className={`col bg-light fw-bold mt-2 ${css.mapControlButton}`}
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "3px",
                    color: "gray",
                  }}
                  icon={faBan}
                  onClick={onCancelEdit}
                />
              </ConditionalRender>
            </Col>
          </MapControl>

          {isLoaded && incomingGeofences && (
            <GeofencesDisplay
              geofences={incomingGeofences}
              onRegularGeofenceClick={(geofence, e) => handleMapClick(e)}
              colorIfActive
              baseGeofenceStyle={{ cursor: "crosshair" }}
            />
          )}

          {/*{isLoaded && incomingGeofences &&*/}
          {/*  incomingGeofences.map((geofence, index) => {*/}
          {/*    // Map the points array to LatLngLiteral objects*/}
          {/*    const paths = geofence.points.map((point) => ({*/}
          {/*      lat: point.latitude,*/}
          {/*      lng: point.longitude,*/}
          {/*    }));*/}

          {/*    const options = {*/}
          {/*      ...optionsForGeofence(geofence),*/}
          {/*      cursor: "crosshair",*/}
          {/*    };*/}

          {/*    return (*/}
          {/*      <PolygonF*/}
          {/*        onMouseOver={() => handleWorkOrderMouseOver(geofence)}*/}
          {/*        key={index}*/}
          {/*        path={paths}*/}
          {/*        options={options}*/}
          {/*        onClick={handleMapClick}*/}
          {/*      />*/}
          {/*    );*/}
          {/*  })}*/}
        </GoogleMap>
      )}
    </>
  );
};

MapContainer.defaultProps = {
  geofences: [],
  containerStyle: defaultContainerStyle,
};

export default React.memo(MapContainer);
